/**
 * API service for AI Agents
 */

const API_BASE_URL = 'https://universal-ai-agent-backend.diypa571.workers.dev'

export interface Agent {
  id: number | string
  agent_id: string
  name: string
  description?: string | null
  system_prompt: string
  is_active: boolean
  created_at: string
  updated_at?: string
  last_used?: string | null
  usage_count?: number
  company_id?: number | string
  color_theme?: string
  agent_names?: string[] | null
}

export interface CreateAgentRequest {
  name: string
  description?: string
  systemPrompt: string
  modelId: number
  colorTheme?: string
  agentNames?: string[]
}

export interface UpdateAgentRequest {
  name?: string
  description?: string
  systemPrompt?: string
  modelId?: number
  colorTheme?: string
  agentNames?: string[]
}

class AgentsService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    const token = localStorage.getItem('token')
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token || ''}`
    }
  }

  async getAgents(): Promise<Agent[]> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${API_BASE_URL}/api/agents`, {
        method: 'GET',
        headers
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Failed to fetch agents')
      }

      const data = await response.json()
      return data.data || []
    } catch (error) {
      console.error('Get agents error:', error)
      throw error
    }
  }

  async createAgent(request: CreateAgentRequest): Promise<Agent> {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.')
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
      
      console.log('Creating agent with request:', request)
      console.log('Token present:', !!token)
      
      const response = await fetch(`${API_BASE_URL}/api/agents`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request)
      })

      console.log('Response status:', response.status)
      const responseData = await response.json().catch(() => ({}))
      console.log('Response data:', responseData)

      if (!response.ok) {
        const errorMessage = responseData.message || responseData.error || `Failed to create agent: ${response.status}`
        throw new Error(errorMessage)
      }

      return responseData.data
    } catch (error) {
      console.error('Create agent error:', error)
      throw error
    }
  }

  async getAgentToken(): Promise<string> {
    try {
      const headers = await this.getAuthHeaders()
      const response = await fetch(`${API_BASE_URL}/api/agent-token`, {
        method: 'GET',
        headers
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({}))
        throw new Error(error.message || 'Failed to get agent token')
      }

      const data = await response.json()
      return data.data?.token || ''
    } catch (error) {
      console.error('Get agent token error:', error)
      throw error
    }
  }

  async updateAgent(agentId: number | string, request: UpdateAgentRequest): Promise<Agent> {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.')
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }

      const response = await fetch(`${API_BASE_URL}/api/agents/${agentId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(request)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to update agent: ${response.status}`)
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      console.error('Update agent error:', error)
      throw error
    }
  }

  async deleteAgent(agentId: number | string): Promise<void> {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.')
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }

      const response = await fetch(`${API_BASE_URL}/api/agents/${agentId}`, {
        method: 'DELETE',
        headers
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `Failed to delete agent: ${response.status}`)
      }
    } catch (error) {
      console.error('Delete agent error:', error)
      throw error
    }
  }
}

export const agentsService = new AgentsService()

